import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import { SILENCE_CAUSES, SILENCE_CAUSE_DISPLAY } from "../../vocabulary";
import type { SilenceCause } from "../../types";

/** The backend's default and its typo guard (ADR 0017) — mirrored here only
 *  so the field can prefill and validate before the round-trip. */
const DEFAULT_DAYS = 10;
const MAX_DAYS = 365;

const PRESETS = [3, 7, 10, 14, 30];

/**
 * Defer an incident (ADR 0017) — and since the false-positive close was
 * retired, the only disposition a human can record that is not a fix.
 *
 * Deliberately **no reason field** — do not add one. The instrument is
 * frictionless on purpose: its first real use is triaging a synchronized
 * day-one cohort, and one that demands prose per hotel gets worked around
 * with the status override, which is the wrong tool for it. The note is
 * offered but optional, and so is the benign cause the retired close used to
 * demand — it feeds the codification counter without taxing an operator who
 * only wants quiet. `other` is the one that owes a note.
 *
 * The copy's job is to say what silencing does NOT do, because "silence"
 * reads like "ignore" and this is the opposite of that: the incident stays
 * open, the hotel stays Unhealthy, and the row stays on the worklist.
 */
export function SilenceModal({
  hotelName,
  onSubmit,
  onClose,
  pending,
}: {
  hotelName: string;
  onSubmit: (body: {
    days: number;
    note?: string;
    cause?: SilenceCause;
  }) => void;
  onClose: () => void;
  pending: boolean;
}) {
  const [days, setDays] = useState<string>(String(DEFAULT_DAYS));
  const [note, setNote] = useState("");
  const [cause, setCause] = useState<SilenceCause | "">("");

  const parsed = Number(days);
  const noteRequired = cause === "other";
  const valid =
    Number.isInteger(parsed) &&
    parsed >= 1 &&
    parsed <= MAX_DAYS &&
    (!noteRequired || !!note.trim());

  const until = valid
    ? new Date(Date.now() + parsed * 86_400_000).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <ActionModal
      title="Silence incident"
      submitLabel="Silence"
      onSubmit={() =>
        onSubmit({
          days: parsed,
          note: note.trim() || undefined,
          cause: cause || undefined,
        })
      }
      onClose={onClose}
      pending={pending}
      disabled={!valid}
    >
      <p className="text-sm text-content-secondary">
        Goes quiet on <strong>{hotelName}</strong> and nothing else. The
        incident stays open, the hotel stays Unhealthy, and it stays on the
        worklist showing this silence.
      </p>

      <div>
        <label className={FIELD_LABEL_CLASS}>Silence for</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setDays(String(preset))}
              className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                parsed === preset
                  ? "border-brand-primary bg-[#E7F1F3] text-brand-primary"
                  : "border-border-secondary text-content-secondary hover:bg-surface-hover"
              }`}
            >
              {preset}d
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={MAX_DAYS}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className={`${INPUT_CLASS} w-24`}
          />
          <span className="text-sm text-content-secondary">
            days
            {until && <> · returns {until}</>}
          </span>
        </div>
        {!valid && (
          <p className="text-xs text-red-600 mt-1">
            Whole number of days, 1–{MAX_DAYS}.
          </p>
        )}
      </div>

      <div>
        <label className={FIELD_LABEL_CLASS}>
          Benign cause (optional)
          <span className="ml-1 font-normal text-gray-500">
            — if you already know why the count looked wrong
          </span>
        </label>
        <select
          value={cause}
          onChange={(e) => setCause(e.target.value as SilenceCause | "")}
          className={INPUT_CLASS}
        >
          <option value="">Not sure yet</option>
          {SILENCE_CAUSES.map((value) => (
            <option key={value} value={value}>
              {SILENCE_CAUSE_DISPLAY[value]}
              {value === "other" ? " (note required)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={FIELD_LABEL_CLASS}>
          Note{noteRequired ? " (required)" : " (optional)"}
        </label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. suspected false positive — waiting on the September booking cycle"
          className={INPUT_CLASS}
        />
        {noteRequired && !note.trim() && (
          <p className="text-xs text-red-600 mt-1">
            “Other” needs a note — it is what makes the cause queryable later.
          </p>
        )}
      </div>

      <p className="text-xs text-gray-400">
        It expires on its own — nothing to undo, and nothing the fleet does
        ends it early. One exception: a booking recorded with no value (Rule 2)
        still posts to Slack, because it reports each new $0 booking once and
        cannot repeat itself. The silence keeps running.
      </p>
    </ActionModal>
  );
}
